import React, { useState } from 'react';
import { useFormContext } from '../context/FormContext';

interface NavigationProps {
  onSubmit?: () => void;
}

export default function Navigation({ onSubmit }: NavigationProps) {
  const { state, previousSection, nextSection, submitForm, sendSaveLink, saveNow, getSubmitBlockers } = useFormContext();
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [saveExitState, setSaveExitState] = useState<'idle' | 'saving' | 'done'>('idle');
  const [showBlockers, setShowBlockers] = useState(false);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [resumeEmail, setResumeEmail] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const submitBlockers = state.currentSection === 9 ? getSubmitBlockers() : [];
  const canSubmit = submitBlockers.length === 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      setShowBlockers(true);
      return;
    }
    setShowBlockers(false);
    if (onSubmit) {
      await onSubmit();
    } else {
      await submitForm();
    }
  };

  const handleSave = async () => {
    setSaveState('saving');
    try {
      await saveNow();
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('idle');
    }
  };

  const handleSaveExit = async () => {
    setSaveExitState('saving');
    const result = await sendSaveLink();
    if (result) {
      setMagicLink(result.magic_link);
      setEmailSent(result.email_sent);
      setResumeEmail(result.seller_email);
    }
    setSaveExitState('done');
  };

  const handleCopyLink = () => {
    if (magicLink) {
      navigator.clipboard.writeText(magicLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const saveButtonLabel =
    saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Saved!' : 'Save';

  return (
    <div className="bg-white border-t border-gray-200 sticky bottom-0 z-10">
      <div className="max-w-4xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Previous Button */}
          <button
            onClick={previousSection}
            disabled={state.currentSection === 1}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          {/* Action Buttons */}
          <div className="flex gap-3 items-center">
            {/* Download PDF */}
            <a
              href="/seller-homework.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 border border-brand-primary text-brand-primary rounded-lg hover:bg-brand-primary hover:text-white transition-colors no-print text-sm"
            >
              Download PDF
            </a>

            {/* Save */}
            <button
              onClick={handleSave}
              disabled={saveState === 'saving' || (!state.isDirty && saveState === 'idle')}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors no-print text-sm disabled:opacity-50"
            >
              {saveButtonLabel}
            </button>

            {/* Save & Exit */}
            <button
              onClick={handleSaveExit}
              disabled={saveExitState === 'saving'}
              className="px-4 py-2 border border-brand-gold text-brand-primary rounded-lg hover:bg-brand-gold hover:text-white transition-colors no-print text-sm disabled:opacity-50"
            >
              {saveExitState === 'saving' ? 'Saving...' : 'Save & Exit'}
            </button>

            {/* Submit/Next Button */}
            {state.currentSection === 9 ? (
              <button
                onClick={handleSubmit}
                disabled={state.isLoading}
                className={`px-6 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  canSubmit
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-gray-300 text-gray-700 hover:bg-gray-400 cursor-pointer'
                }`}
              >
                {state.isLoading ? 'Submitting...' : 'Submit Form'}
              </button>
            ) : (
              <button
                onClick={nextSection}
                className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-dark transition-colors"
              >
                Next Section
              </button>
            )}
          </div>
        </div>

        {/* Save & Exit confirmation banner */}
        {saveExitState === 'done' && magicLink && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm font-medium text-green-800 mb-1">Progress saved!</p>
            {emailSent && resumeEmail ? (
              <>
                <p className="text-sm text-green-700">
                  We've emailed your personal return link to{' '}
                  <span className="font-medium">{resumeEmail}</span>. Open that email anytime
                  to pick up right where you left off — your progress is safe.
                </p>
                <p className="text-xs text-green-600 mt-1">
                  Don't see it? Check your spam folder, or use the backup link below.
                </p>
                <details className="mt-2">
                  <summary className="text-xs text-green-700 cursor-pointer">Show backup link</summary>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs font-mono text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 flex-1 truncate">
                      {magicLink}
                    </span>
                    <button
                      onClick={handleCopyLink}
                      className={`px-3 py-1 rounded text-xs transition-colors ${
                        copied
                          ? 'bg-green-600 text-white'
                          : 'bg-brand-primary text-white hover:bg-brand-primary-dark'
                      }`}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                </details>
              </>
            ) : resumeEmail ? (
              <>
                <p className="text-xs text-green-700 mb-2">
                  We saved your progress but couldn't email your return link automatically.
                  Copy this link to resume anytime, or ask your agent to send it to{' '}
                  <span className="font-medium">{resumeEmail}</span>:
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 flex-1 truncate">
                    {magicLink}
                  </span>
                  <button
                    onClick={handleCopyLink}
                    className={`px-3 py-1 rounded text-xs transition-colors ${
                      copied
                        ? 'bg-green-600 text-white'
                        : 'bg-brand-primary text-white hover:bg-brand-primary-dark'
                    }`}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-green-700 mb-2">
                  Add your email in <span className="font-medium">Section 1 — Seller Information</span>{' '}
                  so we can email your return link, or copy this link to resume anytime:
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono text-gray-600 bg-white border border-gray-200 rounded px-2 py-1 flex-1 truncate">
                    {magicLink}
                  </span>
                  <button
                    onClick={handleCopyLink}
                    className={`px-3 py-1 rounded text-xs transition-colors ${
                      copied
                        ? 'bg-green-600 text-white'
                        : 'bg-brand-primary text-white hover:bg-brand-primary-dark'
                    }`}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Submit blockers banner */}
        {state.currentSection === 9 && !canSubmit && showBlockers && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm font-medium text-red-800 mb-1">
              Please complete the required fields before submitting:
            </p>
            <ul className="list-disc list-inside space-y-0.5">
              {submitBlockers.map((blocker) => (
                <li key={blocker} className="text-xs text-red-700">{blocker}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Auto-save indicator */}
        {state.isDirty && saveExitState !== 'done' && saveState !== 'saved' && (
          <div className="mt-2 text-center">
            <span className="text-sm text-gray-500">Unsaved changes — auto-saving shortly...</span>
          </div>
        )}
        {!state.isDirty && saveState === 'idle' && saveExitState !== 'done' && (
          <div className="mt-2 text-center">
            <span className="text-sm text-green-600">All changes saved</span>
          </div>
        )}
      </div>
    </div>
  );
}
